import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import SectionCard from './SectionCard';
import api from '../utils/api';

const ACTION_KEYS = ['1', '2', '3', '4', '5'];

const LANE_BY_COLUMN = {
  1: 'Full',
  2: 'Full',
  3: 'Partly',
  4: 'Partly',
  5: 'External',
};

/**
 * Under each building: SS we still have to handle, with the inspection term in parentheses.
 * The last column is the external company's open work.
 * Issued and sent SS are left off the board.
 */
export default function PaperworkBoard() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['paperwork-admin-board'],
    queryFn: async () => (await api.get('/paperwork/admin/board')).data?.data,
  });

  const columns = data?.columns || [];
  const actionColumns = columns.filter((col) => ACTION_KEYS.includes(String(col.id)));
  const externalColumn = columns.find((col) => col.id === 'external');
  const buildings = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = data?.buildings || [];
    if (!q) return list;
    return list.filter((b) => String(b.building_name || '').toLowerCase().includes(q));
  }, [data, search]);

  return (
    <SectionCard
      title="Company board"
      flush
      ruled
      actions={
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search building"
          className="border border-green-300 rounded-lg px-3 py-1.5 text-sm"
        />
      }
    >
      <div className="px-4 pb-4">
        <p className="text-sm text-gray-800 mb-3">
          Under each building, the SS that still need a person, with the inspection term (M, Q, H, Y).
          The bold line on each column is the contract: Full, Partly, or External. Yellow is still to
          do. Issued Form 12A / S-RaD are left off. Open a cell to handle it on that building.
        </p>
        {isLoading && <p className="text-sm text-gray-600">Loading board…</p>}
        {isError && (
          <p className="text-sm text-red-700">{error?.response?.data?.error || 'Board failed'}</p>
        )}
        {!isLoading && !buildings.length && (
          <p className="text-sm text-gray-600">No buildings match.</p>
        )}
        {!!buildings.length && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr>
                  <th className="text-left font-semibold text-gray-900 px-2 py-2 border border-green-300 whitespace-nowrap">
                    Building name
                  </th>
                  <th className="text-left font-semibold text-gray-900 px-2 py-2 border border-green-300 whitespace-nowrap">
                    SS
                  </th>
                  <th className="text-left font-semibold text-gray-900 px-2 py-2 border border-green-300 whitespace-nowrap">
                    Due date
                  </th>
                  {actionColumns.map((col) => {
                    const lane = col.lane || LANE_BY_COLUMN[String(col.id)] || '';
                    return (
                      <th
                        key={col.id}
                        className="text-left px-2 py-2 border border-green-300 min-w-[9rem] align-bottom"
                      >
                        {lane ? <div className="font-bold text-gray-950">{lane}</div> : null}
                        <div className="font-medium text-gray-800 mt-0.5">{col.label}</div>
                      </th>
                    );
                  })}
                  {externalColumn ? (
                    <th className="text-left font-semibold text-gray-900 px-2 py-2 border border-green-300 min-w-[9rem]">
                      {externalColumn.label}
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {buildings.map((building) =>
                  (building.rows || []).map((row, index) => (
                    <tr key={`${building.building_id}-${row.ss_label || 'external'}-${index}`}>
                      {index === 0 ? (
                        <td
                          rowSpan={building.rows.length}
                          className="align-top px-2 py-2 border border-green-300 font-semibold text-gray-900"
                        >
                          {building.building_name}
                        </td>
                      ) : null}
                      <td className="px-2 py-2 border border-green-300 whitespace-nowrap text-gray-900">
                        {row.ss_label}
                      </td>
                      <td className="px-2 py-2 border border-green-300 whitespace-nowrap text-gray-800">
                        {row.due_label}
                      </td>
                      {ACTION_KEYS.map((key) => {
                        const cell = row.cells?.[key];
                        return (
                          <td
                            key={key}
                            className={`border border-green-300 p-1 align-top ${
                              cell ? 'bg-yellow-200 text-yellow-950' : 'bg-green-100'
                            }`}
                          >
                            {cell ? (
                              <button
                                type="button"
                                onClick={() => navigate(building.look_path)}
                                className="block w-full text-left text-xs font-medium px-1 py-1 rounded hover:underline"
                              >
                                {cell.text}
                              </button>
                            ) : null}
                          </td>
                        );
                      })}
                      {index === 0 ? (
                        <td
                          rowSpan={building.rows.length}
                          className={`border border-green-300 p-1 align-top ${
                            row.external ? 'bg-yellow-200 text-yellow-950' : 'bg-green-100'
                          }`}
                        >
                          {row.external ? (
                            <button
                              type="button"
                              onClick={() => navigate(building.look_path)}
                              className="block w-full text-left text-xs font-medium px-1 py-1 rounded hover:underline"
                            >
                              {row.external.text}
                            </button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SectionCard>
  );
}
